import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
  Snackbar,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

const DiscountDetailsDialog = ({
  open,
  onClose,
  businessDate,
  storeId,
  glAccount,
  memoDetail,
  totalAmount,
  firebaseUser,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState([]);
  const [showOrderGuid, setShowOrderGuid] = useState(false);
  const [orderBy, setOrderBy] = useState('orderNumber');
  const [order, setOrder] = useState('asc');
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});

  const handleColumnFilterChange = (columnId, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [columnId]: value,
    }));
  };

  const clearColumnFilter = (columnId) => {
    setColumnFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[columnId];
      return newFilters;
    });
  };

  const handleCopy = () => {
    setCopySnackbarOpen(true);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Filter data based on column filters
  const filteredDetails = useMemo(() => {
    if (!details.length) return details;

    const activeFilters = Object.entries(columnFilters).filter(([_, value]) => value.trim() !== '');
    if (activeFilters.length === 0) return details;

    return details.filter((row) => {
      return activeFilters.every(([columnId, filterValue]) => {
        const cellValue = String(row[columnId] ?? '').toLowerCase();
        const filterLower = filterValue.toLowerCase();
        return cellValue.includes(filterLower);
      });
    });
  }, [details, columnFilters]);

  const sortedDetails = useMemo(() => {
    if (!filteredDetails.length) return filteredDetails;

    return [...filteredDetails].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      // Handle numeric sorting for amount and orderNumber
      if (orderBy === 'amount' || orderBy === 'orderNumber') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        // String sorting
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredDetails, orderBy, order]);

  useEffect(() => {
    if (open && businessDate && storeId && glAccount) {
      fetchDetails();
    }
  }, [open, businessDate, storeId, glAccount]);

  const fetchDetails = async () => {
    if (!firebaseUser) return;

    try {
      setLoading(true);
      setError(null);

      const firebaseUserIdToken = await firebaseUser.getIdToken(true);

      const response = await axios.get('/hd-api/gl-export/discount-details', {
        params: {
          businessDate,
          storeId,
          glAccount,
        },
        headers: {
          Authorization: `Bearer ${firebaseUserIdToken}`,
        },
      });

      setDetails(response.data.data || []);
    } catch (err) {
      console.error('Error fetching discount details:', err);
      setError('Failed to load discount details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Calculate totals from filtered data
  const calculatedTotal = filteredDetails.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);

  // Check if any filters are active
  const hasActiveFilters = Object.values(columnFilters).some((v) => v.trim() !== '');

  // Export to Excel
  const handleExport = () => {
    const exportData = sortedDetails.map((item) => ({
      'Order #': item.orderNumber,
      'Discount Name': item.discountName,
      'Discount Type': item.discountType,
      'Amount': parseFloat(item.amount) || 0,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Discount Details');
    XLSX.writeFile(wb, `discount-details-${storeId}-${businessDate}.xlsx`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '80vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocalOfferIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="span">
            Discount Details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
            {memoDetail} - Store {storeId} - {businessDate}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && details.length === 0 && (
          <Alert severity="info">
            No discount details found for this entry.
          </Alert>
        )}

        {!loading && !error && details.length > 0 && (
          <>
            <TableContainer sx={{ maxHeight: 400, userSelect: 'text' }} onCopy={handleCopy}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {showOrderGuid && (
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                        <TableSortLabel
                          active={orderBy === 'orderGuid'}
                          direction={orderBy === 'orderGuid' ? order : 'asc'}
                          onClick={() => handleRequestSort('orderGuid')}
                        >
                          Order GUID
                        </TableSortLabel>
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'orderNumber'}
                        direction={orderBy === 'orderNumber' ? order : 'asc'}
                        onClick={() => handleRequestSort('orderNumber')}
                      >
                        Order #
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'type'}
                        direction={orderBy === 'type' ? order : 'asc'}
                        onClick={() => handleRequestSort('type')}
                      >
                        Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'discountName'}
                        direction={orderBy === 'discountName' ? order : 'asc'}
                        onClick={() => handleRequestSort('discountName')}
                      >
                        Discount Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="right">
                      <TableSortLabel
                        active={orderBy === 'amount'}
                        direction={orderBy === 'amount' ? order : 'asc'}
                        onClick={() => handleRequestSort('amount')}
                      >
                        Amount
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                  {/* Filter Row */}
                  <TableRow>
                    {showOrderGuid && (
                      <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                        <TextField
                          size="small"
                          variant="outlined"
                          placeholder="Filter..."
                          value={columnFilters['orderGuid'] || ''}
                          onChange={(e) => handleColumnFilterChange('orderGuid', e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                            '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                            minWidth: 80,
                            width: '100%',
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                              </InputAdornment>
                            ),
                            endAdornment: columnFilters['orderGuid'] && (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => clearColumnFilter('orderGuid')} sx={{ p: 0.25 }}>
                                  <ClearIcon sx={{ fontSize: '0.875rem' }} />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['orderNumber'] || ''}
                        onChange={(e) => handleColumnFilterChange('orderNumber', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                          '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['orderNumber'] && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('orderNumber')} sx={{ p: 0.25 }}>
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['type'] || ''}
                        onChange={(e) => handleColumnFilterChange('type', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                          '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['type'] && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('type')} sx={{ p: 0.25 }}>
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['discountName'] || ''}
                        onChange={(e) => handleColumnFilterChange('discountName', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                          '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['discountName'] && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('discountName')} sx={{ p: 0.25 }}>
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['amount'] || ''}
                        onChange={(e) => handleColumnFilterChange('amount', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                          '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['amount'] && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('amount')} sx={{ p: 0.25 }}>
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedDetails.map((detail, index) => (
                    <TableRow
                      key={`${detail.orderGuid}-${detail.checkGuid}-${index}`}
                      sx={{
                        '&:hover': { backgroundColor: '#f5f5f5' },
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                      }}
                    >
                      {showOrderGuid && (
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {detail.orderGuid || '-'}
                        </TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ReceiptIcon sx={{ fontSize: 16, color: 'action.active' }} />
                          {detail.orderNumber || '-'}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={detail.type || 'UNKNOWN'}
                          size="small"
                          variant="outlined"
                          color={detail.type === 'ITEM' ? 'info' : 'default'}
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>{detail.discountName || '-'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500 }}>
                        {formatCurrency(detail.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 2,
                pt: 2,
                borderTop: '1px solid #e0e0e0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {filteredDetails.length} discount{filteredDetails.length !== 1 ? 's' : ''}
                  {hasActiveFilters && ` (filtered from ${details.length})`}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={showOrderGuid}
                      onChange={(e) => setShowOrderGuid(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Show Order GUID</Typography>}
                />
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" color="text.secondary">
                  Calculated Total
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {formatCurrency(calculatedTotal)}
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={details.length === 0}
        >
          Export
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  );
};

export default DiscountDetailsDialog;
